import React from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './style.css';
import fetch from '../../../core/fetch';
import { Globe, Bot, Zap, Search, AlertTriangle, Check, Save, X, ChevronLeft, ChevronRight, Loader2, Sparkles, RefreshCw } from 'lucide-react';

// Import all language files
import esMessages from '../../../messages/es.json';
import enMessages from '../../../messages/en-US.json';
import frMessages from '../../../messages/fr-FR.json';
import itMessages from '../../../messages/it-IT.json';
import ptMessages from '../../../messages/pt-PT.json';

const LANGUAGES = [
  { code: 'es', name: 'Español', label: 'ES', isBase: true },
  { code: 'en-US', name: 'English', label: 'EN' },
  { code: 'fr-FR', name: 'Français', label: 'FR' },
  { code: 'it-IT', name: 'Italiano', label: 'IT' },
  { code: 'pt-PT', name: 'Português', label: 'PT' },
];

const ITEMS_PER_PAGE = 30;

class SmartTranslationEditor extends React.Component {
  constructor(props) {
    super(props);
    
    // Build initial translations map from imported files
    const translationsMap = this.buildTranslationsMap();
    
    this.state = {
      // Data
      translations: translationsMap, // Map: id -> { es: msg, en-US: msg, ... }
      originalTranslations: JSON.parse(JSON.stringify(translationsMap)), // Deep copy for comparison
      
      // Language toggles
      languageStatus: {}, // Map: code -> isActive
      
      // UI State
      searchTerm: '',
      filterMode: 'all', // all, missing, modified
      currentPage: 1,
      
      // Modifications tracking
      modifiedIds: new Set(),
      modifiedLanguages: new Set(), // Which language files need saving
      
      // Loading/Saving
      loading: true,
      saving: false,
      translatingId: null, // ID currently being AI translated
      bulkTranslating: false,
      bulkProgress: { current: 0, total: 0 },
      
      // Messages
      successMessage: null,
      errorMessage: null,
    };
  }
  
  componentDidMount() {
    this.loadLanguageStatus();
  }
  
  buildTranslationsMap() {
    const allMessages = {
      'es': esMessages,
      'en-US': enMessages,
      'fr-FR': frMessages,
      'it-IT': itMessages,
      'pt-PT': ptMessages,
    };
    
    const translationsMap = {};
    
    // Use Spanish as base to get all IDs
    esMessages.forEach(item => {
      translationsMap[item.id] = {
        id: item.id,
        description: item.description,
        defaultMessage: item.defaultMessage,
        files: item.files,
        messages: {}
      };
      
      // Add Spanish message
      translationsMap[item.id].messages['es'] = item.message || '';
    });
    
    // Add other languages
    Object.entries(allMessages).forEach(([langCode, messages]) => {
      if (langCode === 'es') return;
      
      messages.forEach(item => {
        if (translationsMap[item.id]) {
          translationsMap[item.id].messages[langCode] = item.message || '';
        }
      });
    });
    
    return translationsMap;
  }
  
  async loadLanguageStatus() {
    try {
      const query = `
        query {
          getLanguages {
            id
            title
            code
            isActive
          }
        }
      `;
      
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
      });
      
      const { data } = await response.json();
      
      const languageStatus = {};
      (data.getLanguages || []).forEach(lang => {
        languageStatus[lang.code] = lang.isActive;
      });
      
      this.setState({ languageStatus, loading: false });
    } catch (error) {
      console.error('Failed to load language status:', error);
      this.setState({ loading: false, errorMessage: 'Failed to load language status' });
    }
  }
  
  handleToggleLanguage = async (langCode) => {
    const { languageStatus } = this.state;
    const newStatus = !languageStatus[langCode];
    
    // Don't allow deactivating Spanish (base)
    if (langCode === 'es' && !newStatus) {
      this.setState({ errorMessage: 'Cannot deactivate the base language (Spanish)' });
      setTimeout(() => this.setState({ errorMessage: null }), 3000);
      return;
    }
    
    // Optimistic update
    this.setState({
      languageStatus: { ...languageStatus, [langCode]: newStatus }
    });
    
    try {
      // Find language ID from code
      const query = `
        query {
          getLanguages {
            id
            code
          }
        }
      `;
      
      const langResponse = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ query }),
      });
      
      const { data: langData } = await langResponse.json();
      const language = (langData.getLanguages || []).find(l => l.code === langCode);
      
      if (!language) {
        throw new Error('Language not found');
      }
      
      const mutation = `
        mutation ToggleLanguageStatus($id: Int!, $isActive: Boolean!) {
          toggleLanguageStatus(id: $id, isActive: $isActive) {
            status
            errorMessage
          }
        }
      `;
      
      const response = await fetch('/graphql', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          query: mutation,
          variables: { id: language.id, isActive: newStatus },
        }),
      });
      
      const { data, errors } = await response.json();
      
      if (errors || (data.toggleLanguageStatus && data.toggleLanguageStatus.status !== 200)) {
        throw new Error(errors?.[0]?.message || data.toggleLanguageStatus?.errorMessage || 'Failed');
      }
      
      this.setState({
        successMessage: `${langCode} has been ${newStatus ? 'activated' : 'deactivated'}`,
      });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
      
    } catch (error) {
      // Revert
      this.setState({
        languageStatus: { ...languageStatus, [langCode]: !newStatus },
        errorMessage: error.message,
      });
      setTimeout(() => this.setState({ errorMessage: null }), 3000);
    }
  };
  
  handleMessageChange = (id, langCode, newValue) => {
    const { translations, modifiedIds, modifiedLanguages, originalTranslations } = this.state;
    
    const updatedTranslations = { ...translations };
    updatedTranslations[id] = {
      ...updatedTranslations[id],
      messages: {
        ...updatedTranslations[id].messages,
        [langCode]: newValue
      }
    };
    
    // Check if this is actually different from original
    const newModifiedIds = new Set(modifiedIds);
    const newModifiedLanguages = new Set(modifiedLanguages);
    
    const originalValue = originalTranslations[id]?.messages?.[langCode] || '';
    if (newValue !== originalValue) {
      newModifiedIds.add(id);
      newModifiedLanguages.add(langCode);
    } else {
      // Check if any language for this ID is still modified
      let stillModified = false;
      LANGUAGES.forEach(lang => {
        const orig = originalTranslations[id]?.messages?.[lang.code] || '';
        const curr = updatedTranslations[id]?.messages?.[lang.code] || '';
        if (orig !== curr) stillModified = true;
      });
      if (!stillModified) {
        newModifiedIds.delete(id);
      }
    }
    
    this.setState({
      translations: updatedTranslations,
      modifiedIds: newModifiedIds,
      modifiedLanguages: newModifiedLanguages,
    });
  };
  
  handleAITranslate = async (id) => {
    const { translations } = this.state;
    const sourceText = translations[id]?.messages?.['es'];
    
    if (!sourceText || sourceText.trim() === '') {
      this.setState({ errorMessage: 'No Spanish text to translate' });
      setTimeout(() => this.setState({ errorMessage: null }), 3000);
      return;
    }
    
    this.setState({ translatingId: id });
    
    try {
      const response = await fetch('/web-api/admin/translate-ui-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceText,
          sourceLang: 'es',
          targetLangs: ['en', 'fr', 'it', 'pt']
        }),
      });
      
      if (!response.ok) {
        throw new Error('Translation failed');
      }
      
      const result = await response.json();
      
      if (result.success && result.translations) {
        // Map API response to our language codes
        const langMapping = {
          'en': 'en-US',
          'fr': 'fr-FR',
          'it': 'it-IT',
          'pt': 'pt-PT'
        };
        
        Object.entries(result.translations).forEach(([shortCode, translatedText]) => {
          const fullCode = langMapping[shortCode];
          if (fullCode) {
            this.handleMessageChange(id, fullCode, translatedText);
          }
        });
        
        this.setState({ 
          successMessage: `✅ Translated "${sourceText.substring(0, 30)}..." to 4 languages`,
          translatingId: null 
        });
        setTimeout(() => this.setState({ successMessage: null }), 3000);
      } else {
        throw new Error(result.error || 'Translation failed');
      }
    } catch (error) {
      this.setState({ 
        errorMessage: `Translation error: ${error.message}`,
        translatingId: null 
      });
      setTimeout(() => this.setState({ errorMessage: null }), 5000);
    }
  };
  
  handleBulkTranslate = async () => {
    const { translations } = this.state;
    
    // Find all IDs that have Spanish but missing other languages
    const idsToTranslate = [];
    Object.entries(translations).forEach(([id, data]) => {
      const esText = data.messages?.['es'];
      if (!esText || esText.trim() === '') return;
      
      // Check if any language is missing
      const hasMissing = LANGUAGES.some(lang => {
        if (lang.code === 'es') return false;
        const msg = data.messages?.[lang.code];
        return !msg || msg.trim() === '';
      });
      
      if (hasMissing) {
        idsToTranslate.push(id);
      }
    });
    
    if (idsToTranslate.length === 0) {
      this.setState({ successMessage: 'All translations are complete!' });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
      return;
    }
    
    this.setState({
      bulkTranslating: true,
      bulkProgress: { current: 0, total: idsToTranslate.length }
    });
    
    for (let i = 0; i < idsToTranslate.length; i++) {
      const id = idsToTranslate[i];
      
      this.setState({
        bulkProgress: { current: i + 1, total: idsToTranslate.length }
      });
      
      await this.handleAITranslate(id);
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    this.setState({
      bulkTranslating: false,
      successMessage: `✅ Translated ${idsToTranslate.length} items to all languages!`
    });
    setTimeout(() => this.setState({ successMessage: null }), 5000);
  };
  
  handleForceTranslateAll = async () => {
    const { translations } = this.state;
    
    // Get ALL IDs that have Spanish text (force overwrite)
    const idsToTranslate = [];
    Object.entries(translations).forEach(([id, data]) => {
      const esText = data.messages?.['es'];
      if (esText && esText.trim() !== '') {
        idsToTranslate.push(id);
      }
    });
    
    if (idsToTranslate.length === 0) {
      this.setState({ errorMessage: 'No Spanish texts found to translate' });
      setTimeout(() => this.setState({ errorMessage: null }), 3000);
      return;
    }
    
    // Confirm with user
    const confirmed = window.confirm(
      `⚠️ FORCE TRANSLATE ALL\n\n` +
      `This will re-translate ALL ${idsToTranslate.length} entries to all languages.\n` +
      `Existing translations will be OVERWRITTEN.\n\n` +
      `This may take several minutes.\n\n` +
      `Continue?`
    );
    
    if (!confirmed) return;
    
    this.setState({
      bulkTranslating: true,
      bulkProgress: { current: 0, total: idsToTranslate.length },
      successMessage: null,
      errorMessage: null
    });
    
    let successCount = 0;
    let errorCount = 0;
    
    for (let i = 0; i < idsToTranslate.length; i++) {
      const id = idsToTranslate[i];
      
      this.setState({
        bulkProgress: { current: i + 1, total: idsToTranslate.length }
      });
      
      try {
        await this.handleAITranslateForce(id);
        successCount++;
      } catch (err) {
        console.error(`Error translating ${id}:`, err);
        errorCount++;
      }
      
      // Delay to avoid rate limiting (100ms between requests)
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    this.setState({
      bulkTranslating: false,
      successMessage: `✅ Force translated ${successCount} items! ${errorCount > 0 ? `(${errorCount} errors)` : ''}`
    });
    setTimeout(() => this.setState({ successMessage: null }), 5000);
  };
  
  // Force translate - overwrites existing translations
  handleAITranslateForce = async (id) => {
    const { translations, modifiedIds, modifiedLanguages } = this.state;
    const item = translations[id];
    
    if (!item) return;
    
    const esText = item.messages?.['es'];
    if (!esText || esText.trim() === '') return;
    
    // Get all non-base language codes
    const targetLangs = LANGUAGES
      .filter(lang => lang.code !== 'es')
      .map(lang => lang.code.split('-')[0]); // en-US -> en
    
    try {
      const response = await fetch('/web-api/admin/translate-ui-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          sourceText: esText,
          sourceLang: 'es',
          targetLangs: targetLangs
        }),
      });
      
      const result = await response.json();
      
      if (result.success && result.translations) {
        // Map back: en -> en-US, fr -> fr-FR, etc.
        const langMapping = {
          'en': 'en-US',
          'fr': 'fr-FR',
          'it': 'it-IT',
          'pt': 'pt-PT'
        };
        
        Object.entries(result.translations).forEach(([shortCode, translation]) => {
          const fullCode = langMapping[shortCode] || shortCode;
          if (translations[id]) {
            translations[id].messages[fullCode] = translation;
            modifiedLanguages.add(fullCode);
          }
        });
        
        modifiedIds.add(id);
        
        this.setState({
          translations: { ...translations },
          modifiedIds: new Set(modifiedIds),
          modifiedLanguages: new Set(modifiedLanguages)
        });
      }
    } catch (error) {
      console.error(`Error force translating ${id}:`, error);
      throw error;
    }
  };
  
  handleSaveAll = async () => {
    const { translations, modifiedLanguages } = this.state;
    
    if (modifiedLanguages.size === 0) {
      this.setState({ successMessage: 'No changes to save' });
      setTimeout(() => this.setState({ successMessage: null }), 3000);
      return;
    }
    
    this.setState({ saving: true });
    
    try {
      // Group translations by language code
      const messagesByLang = {};
      LANGUAGES.forEach(lang => {
        messagesByLang[lang.code] = [];
      });
      
      // Build the array format expected by the save endpoint
      Object.values(translations).forEach(item => {
        LANGUAGES.forEach(lang => {
          messagesByLang[lang.code].push({
            id: item.id,
            description: item.description,
            defaultMessage: item.defaultMessage,
            message: item.messages[lang.code] || '',
            files: item.files
          });
        });
      });
      
      // Save each modified language
      const savePromises = Array.from(modifiedLanguages).map(async langCode => {
        // Handle the es -> es mapping (no suffix)
        const fileCode = langCode;
        
        const response = await fetch('/admin/update-language-file', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            code: fileCode,
            messages: messagesByLang[langCode]
          }),
        });
        
        if (!response.ok) {
          throw new Error(`Failed to save ${langCode}`);
        }
        
        return response.json();
      });
      
      await Promise.all(savePromises);
      
      // Reset modified tracking
      this.setState({
        originalTranslations: JSON.parse(JSON.stringify(translations)),
        modifiedIds: new Set(),
        modifiedLanguages: new Set(),
        saving: false,
        successMessage: `✅ Saved ${modifiedLanguages.size} language file(s) successfully!`
      });
      setTimeout(() => this.setState({ successMessage: null }), 5000);
      
    } catch (error) {
      this.setState({
        saving: false,
        errorMessage: `Save failed: ${error.message}`
      });
      setTimeout(() => this.setState({ errorMessage: null }), 5000);
    }
  };
  
  handleDiscardChanges = () => {
    const { originalTranslations } = this.state;
    
    this.setState({
      translations: JSON.parse(JSON.stringify(originalTranslations)),
      modifiedIds: new Set(),
      modifiedLanguages: new Set(),
      successMessage: 'Changes discarded'
    });
    setTimeout(() => this.setState({ successMessage: null }), 3000);
  };
  
  getFilteredTranslations() {
    const { translations, searchTerm, filterMode } = this.state;
    
    let filtered = Object.values(translations);
    
    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        if (item.id.toLowerCase().includes(term)) return true;
        return Object.values(item.messages).some(msg => 
          msg && msg.toLowerCase().includes(term)
        );
      });
    }
    
    // Mode filter
    if (filterMode === 'missing') {
      filtered = filtered.filter(item => {
        return LANGUAGES.some(lang => {
          const msg = item.messages[lang.code];
          return !msg || msg.trim() === '';
        });
      });
    } else if (filterMode === 'modified') {
      filtered = filtered.filter(item => this.state.modifiedIds.has(item.id));
    }
    
    return filtered;
  }
  
  getMissingCount() {
    const { translations } = this.state;
    let count = 0;
    
    Object.values(translations).forEach(item => {
      LANGUAGES.forEach(lang => {
        if (lang.code === 'es') return; // Don't count base language
        const msg = item.messages[lang.code];
        if (!msg || msg.trim() === '') count++;
      });
    });
    
    return count;
  }
  
  render() {
    const {
      translations,
      languageStatus,
      searchTerm,
      filterMode,
      currentPage,
      modifiedIds,
      loading,
      saving,
      translatingId,
      bulkTranslating,
      bulkProgress,
      successMessage,
      errorMessage,
    } = this.state;
    
    if (loading) {
      return (
        <div className={s.root}>
          <div className={s.loadingSpinner}>Loading translations...</div>
        </div>
      );
    }
    
    const filteredTranslations = this.getFilteredTranslations();
    const totalPages = Math.ceil(filteredTranslations.length / ITEMS_PER_PAGE);
    const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
    const pageTranslations = filteredTranslations.slice(startIdx, startIdx + ITEMS_PER_PAGE);
    const missingCount = this.getMissingCount();
    
    return (
      <div className={s.root}>
        <div className={s.container}>
          {/* Header */}
          <div className={s.headerSection}>
            <h1 className={s.title}><Globe size={24} style={{marginRight: 8, verticalAlign: 'middle'}} /> Smart Translation Editor</h1>
            <div className={s.bulkActions}>
              <button
                className={`${s.bulkButton} ${s.primary}`}
                onClick={this.handleBulkTranslate}
                disabled={bulkTranslating || missingCount === 0}
              >
                {bulkTranslating 
                  ? <><Loader2 size={16} className={s.spinning} /> Translating... ({bulkProgress.current}/{bulkProgress.total})</>
                  : <><Bot size={16} /> Auto-translate Missing ({missingCount})</>
                }
              </button>
              <button
                className={`${s.bulkButton} ${s.warning}`}
                onClick={this.handleForceTranslateAll}
                disabled={bulkTranslating}
                title="Force re-translate ALL entries (overwrites existing translations)"
              >
                {bulkTranslating 
                  ? <><Loader2 size={16} className={s.spinning} /> Translating...</>
                  : <><Zap size={16} /> Force Translate All</>
                }
              </button>
            </div>
          </div>
          
          {/* Messages */}
          {successMessage && (
            <div className={`${s.alert} ${s.alertSuccess}`}><Check size={16} style={{marginRight: 6}} />{successMessage}</div>
          )}
          {errorMessage && (
            <div className={`${s.alert} ${s.alertError}`}><AlertTriangle size={16} style={{marginRight: 6}} />{errorMessage}</div>
          )}
          
          {/* Language Toggles */}
          <div className={s.languageToggles}>
            {LANGUAGES.map(lang => (
              <div 
                key={lang.code}
                className={`${s.languageToggle} ${languageStatus[lang.code] ? s.active : s.inactive}`}
              >
                <span className={s.langBadge}>{lang.label}</span>
                <span className={s.languageLabel}>{lang.name}</span>
                <label className={s.switch}>
                  <input
                    type="checkbox"
                    checked={languageStatus[lang.code] || false}
                    onChange={() => this.handleToggleLanguage(lang.code)}
                    disabled={lang.isBase}
                  />
                  <span className={s.slider}></span>
                </label>
              </div>
            ))}
          </div>
          
          {/* Stats Bar */}
          <div className={s.statsBar}>
            <div className={s.statItem}>
              <span className={s.statValue}>{Object.keys(translations).length}</span>
              <span className={s.statLabel}>Total Keys</span>
            </div>
            <div 
              className={s.statItem} 
              style={{ cursor: 'pointer', padding: '5px 15px', borderRadius: '8px', background: filterMode === 'missing' ? 'rgba(255,255,255,0.2)' : 'transparent' }}
              onClick={() => this.setState({ filterMode: filterMode === 'missing' ? 'all' : 'missing', currentPage: 1 })}
              title="Click to filter missing translations"
            >
              <span className={s.statValue} style={{ color: missingCount > 0 ? '#ffd54f' : '#a5d6a7', display: 'flex', alignItems: 'center', gap: 4 }}>
                {missingCount > 0 ? <><AlertTriangle size={14} /> {missingCount}</> : <><Check size={14} /> 0</>}
              </span>
              <span className={s.statLabel}>Missing</span>
            </div>
            <div className={s.statItem}>
              <span className={s.statValue}>{modifiedIds.size}</span>
              <span className={s.statLabel}>Modified</span>
            </div>
            <div className={s.statItem}>
              <span className={s.statValue}>{filteredTranslations.length}</span>
              <span className={s.statLabel}>Showing</span>
            </div>
          </div>
          
          {/* Search & Filters */}
          <div className={s.searchSection}>
            <div className={s.searchWrapper}>
              <Search size={18} className={s.searchIcon} />
              <input
                type="text"
                className={s.searchInput}
                placeholder="Search by ID or content..."
                value={searchTerm}
                onChange={(e) => this.setState({ searchTerm: e.target.value, currentPage: 1 })}
              />
            </div>
            <select
              className={s.filterSelect}
              value={filterMode}
              onChange={(e) => this.setState({ filterMode: e.target.value, currentPage: 1 })}
            >
              <option value="all">All ({Object.keys(translations).length})</option>
              <option value="missing">Missing ({missingCount})</option>
              <option value="modified">Modified ({modifiedIds.size})</option>
            </select>
            {/* Find Missing Button */}
            <button
              className={`${s.bulkButton} ${s.primary}`}
              onClick={() => this.setState({ filterMode: 'missing', currentPage: 1 })}
              disabled={missingCount === 0}
              style={{
                background: missingCount > 0 
                  ? 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)' 
                  : 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                whiteSpace: 'nowrap'
              }}
            >
              {missingCount > 0 
                ? <><Search size={14} /> Find Missing ({missingCount})</>
                : <><Check size={14} /> All Complete!</>
              }
            </button>
          </div>
          
          {/* Table */}
          <div className={s.tableContainer}>
            <div className={s.tableWrapper}>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th className={s.idCol}>ID</th>
                    {LANGUAGES.map(lang => (
                      <th key={lang.code} className={s.langCol}>
                        <span className={s.thLangBadge}>{lang.label}</span>
                        {lang.isBase && <span className={s.baseBadge}>BASE</span>}
                      </th>
                    ))}
                    <th className={s.actionCol}>AI</th>
                  </tr>
                </thead>
                <tbody>
                  {pageTranslations.map(item => (
                    <tr key={item.id}>
                      <td className={s.idCell} title={item.description}>
                        <div className={s.idText}>{item.id}</div>
                        <div className={s.defaultMessage}>{item.defaultMessage}</div>
                      </td>
                      {LANGUAGES.map(lang => {
                        const value = item.messages[lang.code] || '';
                        const originalValue = this.state.originalTranslations[item.id]?.messages?.[lang.code] || '';
                        const isModified = value !== originalValue;
                        const isEmpty = !value || value.trim() === '';
                        
                        return (
                          <td key={lang.code} className={s.editableCell}>
                            <textarea
                              className={`${s.editInput} ${isModified ? s.modified : ''} ${isEmpty && !lang.isBase ? s.empty : ''} ${lang.isBase ? s.baseLanguage : ''}`}
                              value={value}
                              onChange={(e) => this.handleMessageChange(item.id, lang.code, e.target.value)}
                              rows={1}
                              placeholder={isEmpty ? '(empty)' : ''}
                            />
                          </td>
                        );
                      })}
                      <td>
                        <button
                          className={`${s.aiButton} ${translatingId === item.id ? s.translating : ''}`}
                          onClick={() => this.handleAITranslate(item.id)}
                          disabled={translatingId !== null || !item.messages['es']}
                          title="Translate from Spanish to all languages"
                        >
                          {translatingId === item.id ? <Loader2 size={14} className={s.spinning} /> : <Bot size={14} />}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            <div className={s.pagination}>
              <button
                className={s.pageButton}
                onClick={() => this.setState({ currentPage: currentPage - 1 })}
                disabled={currentPage <= 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              <span className={s.pageInfo}>
                Page {currentPage} of {totalPages || 1}
              </span>
              <button
                className={s.pageButton}
                onClick={() => this.setState({ currentPage: currentPage + 1 })}
                disabled={currentPage >= totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
          
          {/* Save Bar (Fixed at bottom when there are changes) */}
          <div className={`${s.saveBar} ${modifiedIds.size > 0 ? s.visible : ''}`}>
            <div className={s.saveBarInfo}>
              <span className={s.saveBarCount}>
                {modifiedIds.size} translation(s) modified
              </span>
            </div>
            <div className={s.saveBarActions}>
              <button
                className={s.saveButton}
                onClick={this.handleSaveAll}
                disabled={saving}
              >
                {saving ? <><Loader2 size={16} className={s.spinning} /> Saving...</> : <><Save size={16} /> Save All Changes</>}
              </button>
              <button
                className={s.discardButton}
                onClick={this.handleDiscardChanges}
                disabled={saving}
              >
                <X size={16} /> Discard
              </button>
            </div>
          </div>
          
          {/* Bulk Translation Progress Modal */}
          {bulkTranslating && (
            <div className={s.progressOverlay}>
              <div className={s.progressModal}>
                <div className={s.progressTitle}><Bot size={20} style={{marginRight: 8}} /> AI Translation in Progress</div>
                <div className={s.progressBar}>
                  <div 
                    className={s.progressFill}
                    style={{ width: `${(bulkProgress.current / bulkProgress.total) * 100}%` }}
                  />
                </div>
                <div className={s.progressText}>
                  {bulkProgress.current} / {bulkProgress.total} items translated
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(s)(SmartTranslationEditor);
